import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  listPricing,
  listProductsLite,
  updatePricing,
  type PricingPromotionType,
  type PricingRule,
  type PricingTicketType,
  type ProductLite,
} from '../lib/api-client';

const TICKET_TYPE_LABEL: Record<PricingTicketType, string> = {
  adult: '成人',
  senior: '敬老',
  child: '兒童',
  child_3_5: '兒童 3-5 歲',
  child_6_11: '兒童 6-11 歲',
  infant: '幼兒',
  early_bird: '早鳥',
  package_family: '家庭套裝',
  custom: '自訂',
};

const PROMOTION_TYPE_LABEL: Record<PricingPromotionType, string> = {
  discount_pct: '折扣 %',
  fixed_amount_off: '折抵金額',
  bonus: '加贈',
  package_bundle: '組合包',
};

function formatPromotionValue(rule: PricingRule): string {
  switch (rule.promotion_type) {
    case 'discount_pct': {
      const pct = Number(rule.discount_pct);
      return pct > 0 ? `-${pct}%` : '—';
    }
    case 'fixed_amount_off': {
      const amount = rule.fixed_amount_off ? Number(rule.fixed_amount_off) : 0;
      return amount > 0 ? `-NT$ ${amount.toLocaleString()}` : '—';
    }
    case 'bonus':
      return rule.bonus_config
        ? `+${rule.bonus_config.bonus_quantity} × ${rule.bonus_config.bonus_product_id.slice(0, 8)}…`
        : '—';
    case 'package_bundle':
      return rule.package_config
        ? `組合 NT$ ${Number(rule.package_config.bundle_price).toLocaleString()}（${rule.package_config.items.length} 項）`
        : '—';
    default:
      return '—';
  }
}

function productNameOf(products: ProductLite[] | undefined, id: string): string {
  if (!products) return id.slice(0, 8) + '…';
  const p = products.find((x) => x.id === id);
  if (!p) return id.slice(0, 8) + '…';
  return p.name_i18n['zh-TW'] ?? p.sku;
}

export function PricingListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState<string>('');

  const productsQuery = useQuery({
    queryKey: ['pricing.products-lookup'],
    queryFn: listProductsLite,
  });

  const pricingQuery = useQuery({
    queryKey: ['pricing.list', productId],
    queryFn: () => listPricing(productId ? { product_id: productId } : {}),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updatePricing(id, { is_active: isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing.list'] });
    },
  });

  const products: ProductLite[] = productsQuery.data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">定價推廣（A-3）</h1>
        <div className="page-head__actions">
          <Button variant="primary" onClick={() => navigate('/pricing/new')}>
            + 新增定價
          </Button>
          <Button
            variant="secondary"
            onClick={() => pricingQuery.refetch()}
            disabled={pricingQuery.isFetching}
          >
            {pricingQuery.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        票價 / 優惠規則 CRUD（AC-A3-1）；通路 / 星期 / 階梯限制（AC-A3-2）由表單編輯。
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 18px' }}>
        <label style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>商品篩選</label>
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          disabled={productsQuery.isLoading}
          style={{ minWidth: 280 }}
        >
          <option value="">— 全部商品 —</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name_i18n['zh-TW'] ?? p.sku}（{p.sku}）
            </option>
          ))}
        </select>
        {productsQuery.isLoading && (
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>商品載入中…</span>
        )}
      </div>

      {pricingQuery.isLoading && <div>載入中…</div>}
      {pricingQuery.error && (
        <div className="error-banner">{(pricingQuery.error as Error).message}</div>
      )}
      {toggleMutation.error && (
        <div className="error-banner">啟用切換失敗：{(toggleMutation.error as Error).message}</div>
      )}
      {pricingQuery.data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>商品</th>
                <th>票種</th>
                <th>推廣類型</th>
                <th style={{ textAlign: 'right' }}>牌價</th>
                <th>折扣 / 內容</th>
                <th>生效期</th>
                <th>狀態</th>
                <th style={{ width: 200 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {pricingQuery.data.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-cell">
                    {productId ? '此商品尚無定價規則' : '尚無定價規則'}
                  </td>
                </tr>
              )}
              {pricingQuery.data.items.map((r: PricingRule) => (
                <tr key={r.id}>
                  <td style={{ fontSize: 13 }}>{productNameOf(products, r.product_id)}</td>
                  <td>{TICKET_TYPE_LABEL[r.ticket_type]}</td>
                  <td>
                    <Pill dot={false}>{PROMOTION_TYPE_LABEL[r.promotion_type]}</Pill>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    NT$ {Number(r.list_price).toLocaleString()}
                  </td>
                  <td style={{ fontSize: 13 }}>{formatPromotionValue(r)}</td>
                  <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                    {r.effective_from
                      ? new Date(r.effective_from).toLocaleDateString('zh-TW')
                      : '—'}
                    {' ~ '}
                    {r.effective_to ? new Date(r.effective_to).toLocaleDateString('zh-TW') : '—'}
                  </td>
                  <td>
                    {r.is_active ? (
                      <Pill tone="success">啟用</Pill>
                    ) : (
                      <Pill tone="warning">停用</Pill>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="secondary" onClick={() => navigate(`/pricing/${r.id}`)}>
                        編輯
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => toggleMutation.mutate({ id: r.id, isActive: !r.is_active })}
                        disabled={toggleMutation.isPending}
                      >
                        {r.is_active ? '停用' : '啟用'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
